import { SubscriptionPlan } from '@vacationtracker/shared/types/company'

export interface OnboardingV2Progress {
  userId: string
  companyId: string
  completedSteps: string[]
  currentStepIndex: number
  totalSteps: number
  lastUpdated: string
  hasCompletedOnboarding: boolean
}

const STORAGE_KEY = 'vtOnboardingV2Progress'

// Only track these three relevant setup steps
const RELEVANT_STEP_IDS = ['location-setup', 'department-setup', 'leave-request'] as const
const RELEVANT_TOTAL_STEPS = RELEVANT_STEP_IDS.length

const isRelevantStep = (stepId: string): boolean => RELEVANT_STEP_IDS.includes(stepId as (typeof RELEVANT_STEP_IDS)[number])

const filterRelevantSteps = (steps: string[]): string[] => steps.filter(isRelevantStep)

const mapStepIdToRelevantIndex = (stepId: string): number | null => {
  const idx = RELEVANT_STEP_IDS.indexOf(stepId as (typeof RELEVANT_STEP_IDS)[number])
  return idx >= 0 ? idx : null
}

// Custom event name to notify listeners within the same tab
export const ONBOARDING_V2_PROGRESS_EVENT = 'vt:onboardingV2ProgressUpdated'

const emitOnboardingV2ProgressUpdated = (progress: OnboardingV2Progress | null) => {
  try {
    // Dispatch a custom event so other components can react immediately in the same tab
    window.dispatchEvent(new CustomEvent(ONBOARDING_V2_PROGRESS_EVENT, { detail: progress }))
  } catch {
    // no-op in non-browser environments
  }
}

export const getOnboardingV2Progress = (userId: string, companyId: string): OnboardingV2Progress | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) return null

    const raw: OnboardingV2Progress = JSON.parse(stored)

    // Normalize to only relevant steps and total of 3
    const normalized: OnboardingV2Progress = {
      ...raw,
      completedSteps: filterRelevantSteps(raw.completedSteps || []),
      totalSteps: RELEVANT_TOTAL_STEPS,
    }

    // Only return if it matches the current user and company
    if (normalized.userId === userId && normalized.companyId === companyId) {
      return normalized
    }

    return null
  } catch (error) {
    console.error('Error reading onboarding v2 progress from localStorage:', error)
    return null
  }
}

export const saveOnboardingV2Progress = (userId: string, companyId: string, stepId: string, currentStepIndex: number, totalSteps: number, hasCompletedOnboarding = false): void => {
  try {
    const existingProgress = getOnboardingV2Progress(userId, companyId)

    const progress: OnboardingV2Progress = {
      userId,
      companyId,
      completedSteps: existingProgress?.completedSteps || [],
      // Map to relevant index when possible; otherwise keep existing/current
      currentStepIndex: mapStepIdToRelevantIndex(stepId) ?? existingProgress?.currentStepIndex ?? currentStepIndex,
      // Always store the normalized total of relevant steps
      totalSteps: RELEVANT_TOTAL_STEPS,
      lastUpdated: new Date().toISOString(),
      hasCompletedOnboarding,
    }

    // Track only relevant steps
    if (isRelevantStep(stepId) && !progress.completedSteps.includes(stepId)) {
      progress.completedSteps.push(stepId)
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(progress))
    emitOnboardingV2ProgressUpdated(progress)
  } catch (error) {
    console.error('Error saving onboarding v2 progress to localStorage:', error)
  }
}

export const markOnboardingV2Complete = (userId: string, companyId: string): void => {
  try {
    const existingProgress = getOnboardingV2Progress(userId, companyId)

    if (existingProgress) {
      const progress: OnboardingV2Progress = {
        ...existingProgress,
        hasCompletedOnboarding: true,
        lastUpdated: new Date().toISOString(),
      }

      localStorage.setItem(STORAGE_KEY, JSON.stringify(progress))
      emitOnboardingV2ProgressUpdated(progress)
    }
  } catch (error) {
    console.error('Error marking onboarding v2 as complete:', error)
  }
}

/**
 * Marks onboarding as complete for Freemium plan users.
 * This prevents onboarding from showing when switching to Freemium, including
 * the critical signup flow case where users land on dashboard with onboardingV2=true.
 *
 * Creates progress if none exists (signup case) or updates existing progress.
 */
export const markOnboardingCompleteForFreemium = (userId: string, companyId: string): void => {
  try {
    const existingProgress = getOnboardingV2Progress(userId, companyId)

    if (existingProgress) {
      // Update existing progress - mark as complete
      const completedProgress: OnboardingV2Progress = {
        ...existingProgress,
        hasCompletedOnboarding: true,
        lastUpdated: new Date().toISOString(),
      }

      localStorage.setItem(STORAGE_KEY, JSON.stringify(completedProgress))
      emitOnboardingV2ProgressUpdated(completedProgress)
    } else {
      // No progress exists - create one marked as complete
      // This handles the signup case where user lands on dashboard with Freemium
      const completedProgress: OnboardingV2Progress = {
        userId,
        companyId,
        completedSteps: [...RELEVANT_STEP_IDS], // Mark all steps as completed
        currentStepIndex: RELEVANT_TOTAL_STEPS - 1,
        totalSteps: RELEVANT_TOTAL_STEPS,
        lastUpdated: new Date().toISOString(),
        hasCompletedOnboarding: true,
      }

      localStorage.setItem(STORAGE_KEY, JSON.stringify(completedProgress))
      emitOnboardingV2ProgressUpdated(completedProgress)
    }
  } catch (error) {
    console.error('Error disabling onboarding for Freemium plan:', error)
  }
}

export const initializeOnboardingV2Progress = (userId: string, companyId: string, totalSteps: number): void => {
  try {
    const existingProgress = getOnboardingV2Progress(userId, companyId)

    // Only initialize if no progress exists
    if (!existingProgress) {
      const progress: OnboardingV2Progress = {
        userId,
        companyId,
        completedSteps: [],
        currentStepIndex: 0,
        totalSteps: RELEVANT_TOTAL_STEPS,
        lastUpdated: new Date().toISOString(),
        hasCompletedOnboarding: false,
      }

      localStorage.setItem(STORAGE_KEY, JSON.stringify(progress))
      emitOnboardingV2ProgressUpdated(progress)
    }
  } catch (error) {
    console.error('Error initializing onboarding v2 progress:', error)
  }
}

export const shouldShowOnboardingV2Progress = (progress: OnboardingV2Progress | null): boolean => {
  if (!progress) return false

  // Show if onboarding is not complete and user has started onboarding
  // (either has completed steps or is in progress)
  return (
    !progress.hasCompletedOnboarding && (progress.completedSteps.length > 0 || progress.currentStepIndex > 0 || !!progress.lastUpdated) // If there's any progress record, show the button
  )
}
